import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/lib/api";
import { formatDateTime } from "@/lib/date-format";
import { useDateFormat } from "@/lib/useDateFormat";
import { useRole } from "@/lib/useRole";

interface Props {
  pieceId: number;
  publicationDate: string | null;
  fallbackLabel: string;
}

/**
 * S-072 / FEAT-006 (Phase 9). Hono PATCH /pieces/:id replaces the
 * Directus-backed update hook. `publication_date` is the field name on
 * both the Hono route and the legacy Directus column, so the body
 * shape is unchanged from the previous version.
 */
function useSetPublicationDate(pieceId: number) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (iso: string | null) => {
      return api(`/pieces/${pieceId}`, {
        method: "PATCH",
        body: JSON.stringify({ publication_date: iso }),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["piece", pieceId] });
      void qc.invalidateQueries({ queryKey: ["pieces"] });
    },
  });
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

interface YMD {
  y: number;
  m: number;
  d: number;
}

const pad = (n: number) => String(n).padStart(2, "0");
const isoOf = (y: number, m: number, d: number) =>
  `${y}-${pad(m + 1)}-${pad(d)}`;
const daysInMonth = (y: number, m: number) => new Date(y, m + 1, 0).getDate();
const firstDow = (y: number, m: number) => new Date(y, m, 1).getDay();
const addDays = (y: number, m: number, d: number, delta: number): YMD => {
  const nd = new Date(y, m, d);
  nd.setDate(nd.getDate() + delta);
  return { y: nd.getFullYear(), m: nd.getMonth(), d: nd.getDate() };
};

function todayYMD(): YMD {
  const t = new Date();
  return { y: t.getFullYear(), m: t.getMonth(), d: t.getDate() };
}

function parseISODate(iso: string | null | undefined): YMD | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return { y: d.getFullYear(), m: d.getMonth(), d: d.getDate() };
}

function parseISOTime(iso: string | null | undefined): string {
  if (!iso) return "09:00";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "09:00";
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const TIME_OPTIONS = (() => {
  const out: string[] = [];
  for (let h = 6; h <= 20; h++) {
    for (const m of [0, 30]) out.push(`${pad(h)}:${pad(m)}`);
  }
  return out;
})();

const TZ_OPTIONS: Array<{ value: string; iana: string }> = [
  { value: "PT", iana: "America/Los_Angeles" },
  { value: "MT", iana: "America/Denver" },
  { value: "CT", iana: "America/Chicago" },
  { value: "ET", iana: "America/New_York" },
  { value: "UTC", iana: "UTC" },
  { value: "BST", iana: "Europe/London" },
  { value: "CET", iana: "Europe/Berlin" },
];

/**
 * Convert a wall-clock time in a given IANA timezone to a UTC ISO string.
 * Handles DST correctly via Intl.DateTimeFormat — the trick is to ask Intl
 * what time the instant has in the target zone, then shift by the diff.
 */
function wallTimeToUtcIso(
  y: number,
  m: number,
  d: number,
  hour: number,
  minute: number,
  tzIana: string
): string {
  const asUtc = Date.UTC(y, m, d, hour, minute);
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: tzIana,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = fmt.formatToParts(new Date(asUtc));
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  const zonedAsUtc = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour") % 24,
    get("minute")
  );
  const offset = asUtc - zonedAsUtc;
  return new Date(asUtc + offset).toISOString();
}

interface Preset {
  id: string;
  label: string;
  delta?: number;
  compute?: (today: YMD) => YMD;
}

const PRESETS: Preset[] = [
  { id: "tomorrow", label: "Tomorrow", delta: 1 },
  { id: "in-3", label: "In 3 days", delta: 3 },
  {
    id: "next-mon",
    label: "Next Monday",
    compute: (t) => {
      const d = new Date(t.y, t.m, t.d);
      const dow = d.getDay();
      const add = ((1 - dow + 7) % 7) || 7;
      d.setDate(d.getDate() + add);
      return { y: d.getFullYear(), m: d.getMonth(), d: d.getDate() };
    },
  },
  { id: "in-week", label: "In 1 week", delta: 7 },
  { id: "in-2w", label: "In 2 weeks", delta: 14 },
  {
    id: "next-mo",
    label: "Next month",
    compute: (t) => {
      const nm = t.m === 11 ? 0 : t.m + 1;
      const ny = t.m === 11 ? t.y + 1 : t.y;
      return { y: ny, m: nm, d: Math.min(t.d, daysInMonth(ny, nm)) };
    },
  },
];

export function PublicationDateEdit({
  pieceId,
  publicationDate,
  fallbackLabel,
}: Props) {
  const dateFormat = useDateFormat();
  const [open, setOpen] = useState(false);
  const update = useSetPublicationDate(pieceId);
  // FEAT-108 / S-438 — a Reviewer sees the date but cannot change it.
  const { canEdit } = useRole();

  if (!canEdit) {
    return (
      <span className="publish-info">
        <span className="publish-label">{publicationDate ? "Publish" : "Publish date"}</span>
        <span className="publish-date">
          {publicationDate ? formatDateTime(publicationDate, dateFormat) : fallbackLabel}
        </span>
      </span>
    );
  }

  return (
    <>
      <button
        className="publish-info is-button"
        onClick={() => setOpen(true)}
        title="Click to schedule or change the publish date"
      >
        <span className="publish-label">
          {publicationDate ? "Publish" : "Set publish date"}
        </span>
        <span className="publish-date">
          {publicationDate ? formatDateTime(publicationDate, dateFormat) : fallbackLabel}
        </span>
      </button>

      {open && (
        <PublishDateModal
          initialIso={publicationDate}
          onClose={() => setOpen(false)}
          onConfirm={async (iso) => {
            await update.mutateAsync(iso);
            setOpen(false);
          }}
          onClear={
            publicationDate
              ? async () => {
                  await update.mutateAsync(null);
                  setOpen(false);
                }
              : undefined
          }
          saving={update.isPending}
        />
      )}
    </>
  );
}

function PublishDateModal({
  initialIso,
  onClose,
  onConfirm,
  onClear,
  saving,
}: {
  initialIso: string | null;
  onClose: () => void;
  onConfirm: (utcIso: string) => void;
  onClear?: () => void;
  saving?: boolean;
}) {
  const today = todayYMD();
  const initDate = parseISODate(initialIso) ?? today;
  const [sel, setSel] = useState<YMD>(initDate);
  const [view, setView] = useState({ y: initDate.y, m: initDate.m });
  const [time, setTime] = useState(parseISOTime(initialIso));
  const [tz, setTz] = useState("ET");

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const todayKey = isoOf(today.y, today.m, today.d);
  const selKey = isoOf(sel.y, sel.m, sel.d);

  const cells = useMemo(() => {
    const total = daysInMonth(view.y, view.m);
    const lead = firstDow(view.y, view.m);
    const arr: Array<
      | { pad: true; key: string }
      | { pad?: false; key: string; y: number; m: number; d: number }
    > = [];
    for (let i = 0; i < lead; i++) arr.push({ pad: true, key: `lp-${i}` });
    for (let d = 1; d <= total; d++)
      arr.push({ key: isoOf(view.y, view.m, d), y: view.y, m: view.m, d });
    while (arr.length % 7 !== 0)
      arr.push({ pad: true, key: `tp-${arr.length}` });
    return arr;
  }, [view.y, view.m]);

  const stepMonth = (delta: number) => {
    let m = view.m + delta;
    let y = view.y;
    if (m < 0) {
      m = 11;
      y--;
    }
    if (m > 11) {
      m = 0;
      y++;
    }
    setView({ y, m });
  };

  const applyPreset = (p: Preset) => {
    const next = p.compute
      ? p.compute(today)
      : addDays(today.y, today.m, today.d, p.delta ?? 0);
    setSel(next);
    setView({ y: next.y, m: next.m });
  };

  const selDow = DOW[new Date(sel.y, sel.m, sel.d).getDay()];
  const longLabel = `${selDow}, ${MONTHS[sel.m]} ${sel.d}, ${sel.y}`;

  const handleConfirm = () => {
    const [hh, mm] = time.split(":").map(Number);
    const iana =
      TZ_OPTIONS.find((t) => t.value === tz)?.iana || "America/New_York";
    const utc = wallTimeToUtcIso(sel.y, sel.m, sel.d, hh, mm, iana);
    onConfirm(utc);
  };

  return (
    <div
      className="pub-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="pub-modal" role="dialog" aria-labelledby="pub-title">
        <header className="pub-head">
          <div className="pub-head-l">
            <div className="pub-eyebrow">Schedule</div>
            <h2 id="pub-title" className="pub-title">
              Set publish date
            </h2>
          </div>
          <button className="pub-x" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <div className="pub-body">
          <aside className="pub-presets">
            <div className="pub-section-label">Quick pick</div>
            <ul className="pub-preset-list">
              {PRESETS.map((p) => {
                const target = p.compute
                  ? p.compute(today)
                  : addDays(today.y, today.m, today.d, p.delta ?? 0);
                const tKey = isoOf(target.y, target.m, target.d);
                const tLabel = `${MONTHS[target.m].slice(0, 3)} ${target.d}`;
                return (
                  <li key={p.id}>
                    <button
                      className={`pub-preset ${selKey === tKey ? "is-on" : ""}`}
                      onClick={() => applyPreset(p)}
                    >
                      <span className="pub-preset-label">{p.label}</span>
                      <span className="pub-preset-date mono">{tLabel}</span>
                    </button>
                  </li>
                );
              })}
            </ul>

            <div className="pub-tip">
              <span className="pub-tip-bullet">·</span>
              Pieces publish at the chosen time in the selected timezone.
              Editorial holds may delay by up to 24 h.
            </div>
          </aside>

          <div className="pub-cal">
            <div className="pub-cal-head">
              <button
                className="pub-step"
                onClick={() => stepMonth(-1)}
                aria-label="Previous month"
              >
                ‹
              </button>
              <div className="pub-cal-title">
                {MONTHS[view.m]} {view.y}
              </div>
              <button
                className="pub-step"
                onClick={() => stepMonth(1)}
                aria-label="Next month"
              >
                ›
              </button>
            </div>

            <div className="pub-grid">
              {DOW.map((d) => (
                <div key={d} className="pub-dow">
                  {d}
                </div>
              ))}
              {cells.map((c) => {
                if ("pad" in c && c.pad) {
                  return <div key={c.key} className="pub-cell pub-cell-pad" />;
                }
                const cell = c as { key: string; y: number; m: number; d: number };
                const k = cell.key;
                const isSel = k === selKey;
                const isToday = k === todayKey;
                const isPast = k < todayKey;
                return (
                  <button
                    key={k}
                    className={`pub-cell ${isSel ? "is-sel" : ""} ${
                      isToday ? "is-today" : ""
                    } ${isPast ? "is-past" : ""}`}
                    onClick={() =>
                      !isPast && setSel({ y: cell.y, m: cell.m, d: cell.d })
                    }
                    disabled={isPast}
                  >
                    {cell.d}
                  </button>
                );
              })}
            </div>

            <div className="pub-time-row">
              <div className="pub-field">
                <label className="pub-field-label">Time</label>
                <select
                  className="pub-select"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                >
                  {TIME_OPTIONS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div className="pub-field pub-field-tz">
                <label className="pub-field-label">Timezone</label>
                <select
                  className="pub-select"
                  value={tz}
                  onChange={(e) => setTz(e.target.value)}
                >
                  {TZ_OPTIONS.map((z) => (
                    <option key={z.value} value={z.value}>
                      {z.value}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        <footer className="pub-foot">
          <div className="pub-summary">
            <span className="pub-summary-label">Will publish</span>
            <span className="pub-summary-value">
              {longLabel} · {time} {tz}
            </span>
          </div>
          <div className="pub-actions">
            {onClear && (
              <button
                className="pub-btn pub-btn-ghost"
                onClick={onClear}
                disabled={saving}
                title="Clear publication date"
              >
                Clear
              </button>
            )}
            <button
              className="pub-btn pub-btn-ghost"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              className="pub-btn pub-btn-primary"
              onClick={handleConfirm}
              disabled={saving}
            >
              {saving ? "Saving…" : "Schedule"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
